import React from 'react';
import I18n from 'i18n-js';

import {
  RCCallout,
  RCImage,
  RCText,
  RCIcon,
  responsiveDimensions,
} from '@reverbdotcom/cadence/components';
import { CameraIcon, PackageIcon, RulerIcon, XIcon } from '@reverbdotcom/cadence/icons/react';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import SiteModule from '@reverbdotcom/commons/src/components/site_module';
import { I18N } from '@reverbdotcom/commons/src/components/translate';

import UpsLogo from '../../../../images/icons/shipping-icons/ups.svg';
import UspsStackedLogo from '../../../../images/icons/shipping-icons/usps-stacked.svg';
import FedExLogo from '../../../../images/icons/shipping-icons/fedex.svg';
import PrinterLogo from '../../../../images/icons/shipping-icons/printer.svg';
import HazmatIncorrectBox from '../../../../images/shipping/hazmat-incorrect-box.svg';
import HazmatCorrectBox from '../../../../images/shipping/hazmat-correct-box.svg';
import { ShipmentPackingHelpLink } from '../../shipment_packing_help_link';

interface PackageMeasurementProps {
  weight: string;
  length: string;
  height: string;
  width: string;
}

interface IProps {
  hasShippingLabel: boolean,
  packageMeasurements: PackageMeasurementProps,
  carrierDisplay: string | null,
  carrierSlug: string,
  categoryUuids: string[],
  purchaseNewLabelUrl: string,
}

const UPS_SLUG = 'ups';
export const UPS_SUREPOST_SLUG = 'upssurepost';
export const USPS_SLUG = 'usps';
const FEDEX_SLUG = 'fedex';
const FEDEX_SMARTPOST_SLUG = 'fedexsmartpost';
const DEFAULT_SLUG = 'default';

const CARRIERS_WITH_CONTENT = [
  UPS_SLUG,
  UPS_SUREPOST_SLUG,
  USPS_SLUG,
  FEDEX_SLUG,
  FEDEX_SMARTPOST_SLUG,
];

const CARRIER_LOGOS: Record<string, string> = {
  [UPS_SLUG]: UpsLogo,
  [UPS_SUREPOST_SLUG]: UpsLogo,
  [USPS_SLUG]: UspsStackedLogo,
  [FEDEX_SLUG]: FedExLogo,
  [FEDEX_SMARTPOST_SLUG]: FedExLogo,
  [DEFAULT_SLUG]: PrinterLogo,
};

function CarrierCallout({
  carrierDisplay,
  carrierSlug,
}: {
  carrierDisplay: string | null;
  carrierSlug: string;
}) {
  if (!carrierDisplay) return null;

  const carrierKey = CARRIERS_WITH_CONTENT.includes(carrierSlug) ? carrierSlug : DEFAULT_SLUG;
  const carrierLogo = CARRIER_LOGOS[carrierKey];

  return (
    <div className="mb-5">
      <RCCallout
        themeType="surface"
        variant="on-primary"
        title={I18n.t('discovery.selling.order.carrierCallout.title', { carrier: carrierDisplay })}
        image={(
          <div
            style={{
              width: '100%',
              backgroundColor: 'var(--rc-color-palette-bw-white)',
              borderRadius: 'var(--rc-border-radius-md)',
            }}
          >
            <RCImage src={carrierLogo} borderRadius="md" />
          </div>
        )}
      >
        <I18N
          html
          text={`discovery.selling.order.carrierCallout.content.${carrierKey}Html`}
          args={{ carrier: carrierDisplay }}
          className="lh-180"
        />
      </RCCallout>
    </div>
  );
}

function CarrierChargesCallout({ carrierSlug }: { carrierSlug: string }) {
  const uspsPenaltyCopy = carrierSlug === USPS_SLUG
    ? I18n.t('discovery.selling.order.carrierChargesCallout.uspsPenaltySuffix')
    : '';

  return (
    <div className="d-flex fx-dir-col gap-4 tablet-ml-6">
      <RCText.Utility size="500" weight="semibold">
        {I18n.t('discovery.selling.order.carrierChargesCallout.title')}
      </RCText.Utility>
      <div className="d-flex fx-dir-col gap-2">
        <div className="d-flex gap-2">
          <div className="d-flex fx-align-center">
            <PackageIcon size={24} style={{ fill: 'var(--rc-color-accent-notice)' }} />
          </div>
          <RCText.Default size="400" htmlTag="span">
            {`${I18n.t('discovery.selling.order.carrierChargesCallout.labelsAndStickers')}${uspsPenaltyCopy}.`}
          </RCText.Default>
        </div>
        <div className="d-flex gap-2">
          <div className="d-flex fx-align-center">
            <RulerIcon size={24} style={{ fill: 'var(--rc-color-accent-notice)' }} />
          </div>
          <RCText.Default size="400" htmlTag="span">
            {I18n.t('discovery.selling.order.carrierChargesCallout.packageDimensions')}
          </RCText.Default>
        </div>
      </div>
    </div>
  );
}

function PackAndShip({ categoryUuids }: { categoryUuids: string[] }) {
  return (
    <div className="d-flex fx-justify-between fx-align-center mobile-d-block">
      <div className="d-flex fx-align-center">
        <span
          aria-label="Step One"
          className="d-flex fx-align-center fx-justify-center bg-gray color-white size-130 weight-bold"
          style={{ borderRadius: '50%', width: '4rem', height: '4rem', flexShrink: '0' }}
        >
          1
        </span>
        <p className="size-110 mb-0 ml-4 weight-bold">
          {I18n.t('discovery.selling.order.packAndShip')}
        </p>
      </div>
      <div className="ml-4 mobile-mt-2 mobile-ml-0 tablet-d-none">
        <ShipmentPackingHelpLink categoryUuids={categoryUuids} />
      </div>
    </div>
  );
}

function DimensionsCallout({
  weight,
  length,
  height,
  width,
  purchaseNewLabelUrl,
}: PackageMeasurementProps & { purchaseNewLabelUrl: string }) {
  return (
    <div className="d-flex fx-dir-col gap-3 tablet-ml-6">
      <RCText.Default weight="bold">
        {I18n.t('discovery.selling.order.dimensionsCallout.title')}
      </RCText.Default>
      <div className="d-flex gap-6">
        <div>
          L {length} <RCIcon icon={<XIcon />} size="inline" /> W {width} <RCIcon icon={<XIcon />} size="inline" /> H {height}
        </div>
        <div>
          {weight}
        </div>
      </div>
      <div>
        <RCText.Default size="350" htmlTag="span">
          <span className="mr-1">
            {I18n.t('discovery.selling.order.dimensionsCallout.dimensionsDontMatch')}
          </span>
          <CoreLink className="weight-semibold td-underline fg-color-text-primary" to={purchaseNewLabelUrl}>
            {I18n.t('discovery.selling.order.dimensionsCallout.purchaseNewLabel')}
          </CoreLink>
        </RCText.Default>
      </div>
    </div>
  );
}

export default function TimeToShipModuleOne({
  hasShippingLabel,
  packageMeasurements,
  carrierDisplay,
  carrierSlug,
  categoryUuids,
  purchaseNewLabelUrl,
}: IProps) {
  const hazmatImgDimensions = responsiveDimensions({
    height: { desktop: '220px', tablet: '209px', mobile: '133px' },
  });

  return (
    <>
      {hasShippingLabel && <CarrierCallout
        carrierDisplay={carrierDisplay}
        carrierSlug={carrierSlug}
      />}
      <SiteModule>
        <div className="d-flex fx-dir-col gap-8 tablet-gap-6 mobile-gap-8">
          <PackAndShip categoryUuids={categoryUuids} />
          {hasShippingLabel && (
            <div className="d-grid grid-cols-12 gap-8 tablet-gap-6">
              <div className="grid-col-span-7 tablet-grid-col-span-12 d-flex fx-dir-col gap-4 max-width-80 tablet-max-width-100">
                <CarrierChargesCallout carrierSlug={carrierSlug} />
                <div className="bdt-1" style={{ borderColor: 'var(--rc-color-accent-notice)' }}/>
                <div className="d-grid grid-cols-2 tablet-grid-cols-1 gap-4">
                  <DimensionsCallout
                    weight={packageMeasurements.weight}
                    length={packageMeasurements.length}
                    height={packageMeasurements.height}
                    width={packageMeasurements.width}
                    purchaseNewLabelUrl={purchaseNewLabelUrl}
                  />
                  <RCCallout
                    themeType="utility"
                    variant="notice"
                    icon={<CameraIcon weight="duotone" />}
                  >
                    <I18N
                      html
                      text="discovery.selling.order.dimensionsCallout.proTipHtml"
                      args={{ carrier: carrierDisplay }}
                    />
                  </RCCallout>
                </div>
              </div>
              <div className="grid-col-span-5 tablet-grid-col-span-12 d-flex fx-justify-end tablet-fx-justify-start gap-8 tablet-gap-5">
                <img
                  src={HazmatIncorrectBox}
                  alt={I18n.t('discovery.selling.order.hazmatImages.incorrectBoxAlt')}
                  className={hazmatImgDimensions.className}
                  style={hazmatImgDimensions.style}
                />
                <img
                  src={HazmatCorrectBox}
                  alt={I18n.t('discovery.selling.order.hazmatImages.correctBoxAlt')}
                  className={hazmatImgDimensions.className}
                  style={hazmatImgDimensions.style}
                />
              </div>
            </div>
          )}
        </div>
      </SiteModule>
    </>
  );
}
